import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, HeadphonesIcon, PlusIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
const NAMES_ES = [{
  name: 'María G.',
  location: 'Ciudad de México, MX'
}, {
  name: 'Ana S.',
  location: 'Buenos Aires, AR'
}, {
  name: 'Carmen R.',
  location: 'Madrid, ES'
}, {
  name: 'Laura M.',
  location: 'Bogotá, CO'
}, {
  name: 'Patricia L.',
  location: 'Lima, PE'
}, {
  name: 'Isabel V.',
  location: 'Santiago, CL'
}, {
  name: 'Rosa T.',
  location: 'Caracas, VE'
}, {
  name: 'Sofía P.',
  location: 'Quito, EC'
}, {
  name: 'Gabriela H.',
  location: 'Montevideo, UY'
}, {
  name: 'Daniela C.',
  location: 'San José, CR'
}, {
  name: 'Pamela G.',
  location: 'Medellín, CO'
}, {
  name: 'Janeth A.',
  location: 'Guadalajara, MX'
}, {
  name: 'Maribel S.',
  location: 'Santo Domingo, DO'
}, {
  name: 'Angela L.',
  location: 'Panamá, PA'
}, {
  name: 'Gladys M.',
  location: 'La Paz, BO'
}, {
  name: 'Albeiro Q.',
  location: 'Cali, CO'
}, {
  name: 'Vilma R.',
  location: 'Tegucigalpa, HN'
}, {
  name: 'Luz Estela D.',
  location: 'Barranquilla, CO'
}, {
  name: 'Amparo E.',
  location: 'Valencia, ES'
}, {
  name: 'Roger C.',
  location: 'Monterrey, MX'
}, {
  name: 'Jakeline M.',
  location: 'Guayaquil, EC'
}, {
  name: 'Ruth P.',
  location: 'Asunción, PY'
}, {
  name: 'Maritza C.',
  location: 'Managua, NI'
}, {
  name: 'Dora U.',
  location: 'San Salvador, SV'
}, {
  name: 'Cristian M.',
  location: 'Puebla, MX'
}];
const NAMES_EN = [{
  name: 'Sarah M.',
  location: 'New York, NY'
}, {
  name: 'Jennifer L.',
  location: 'Los Angeles, CA'
}, {
  name: 'Emily R.',
  location: 'Chicago, IL'
}, {
  name: 'Jessica T.',
  location: 'Houston, TX'
}, {
  name: 'Ashley K.',
  location: 'Phoenix, AZ'
}, {
  name: 'Amanda S.',
  location: 'Philadelphia, PA'
}, {
  name: 'Melissa W.',
  location: 'San Antonio, TX'
}, {
  name: 'Michelle B.',
  location: 'San Diego, CA'
}, {
  name: 'Stephanie D.',
  location: 'Dallas, TX'
}, {
  name: 'Rebecca H.',
  location: 'Austin, TX'
}];
const STORAGE_KEY = 'notif_shown_indices';
function getShownIndices(lang: string): number[] {
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY}_${lang}`);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveShownIndices(lang: string, indices: number[]) {
  try {
    sessionStorage.setItem(`${STORAGE_KEY}_${lang}`, JSON.stringify(indices));
  } catch {}
}
type PurchaseType = 'ebook' | 'bundle';
interface CurrentNotification {
  name: string;
  location: string;
  type: PurchaseType;
}
export function PurchaseNotification() {
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'es';
  const [isVisible, setIsVisible] = useState(false);
  const [current, setCurrent] = useState<CurrentNotification>({
    ...NAMES_ES[0],
    type: 'ebook'
  });
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  useEffect(() => {
    const names = language === 'es' ? NAMES_ES : NAMES_EN;
    // Clear any pending timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    // Get previously shown indices from sessionStorage
    let previouslyShown = getShownIndices(language);
    // Build available pool (exclude already shown)
    let available = names.map((_, i) => i).filter(i => !previouslyShown.includes(i));
    // If not enough fresh names, reset the pool
    if (available.length < 6) {
      previouslyShown = [];
      available = names.map((_, i) => i);
      saveShownIndices(language, []);
    }
    // Shuffle and pick 6
    const shuffled = available.sort(() => Math.random() - 0.5).slice(0, 6);
    // Save these as shown
    saveShownIndices(language, [...previouslyShown, ...shuffled]);
    // Assign purchase types: ~35% bundle, rest ebook-only
    const types: PurchaseType[] = shuffled.map((_, i) => i === 1 || i === 4 ? 'bundle' : 'ebook');
    // Fixed intervals: 12s, 15s, 20s cycling
    const intervals = [12000, 15000, 20000];
    let cumulativeDelay = 6000;
    shuffled.forEach((personIndex, i) => {
      const showT = setTimeout(() => {
        setCurrent({
          ...names[personIndex],
          type: types[i]
        });
        setIsVisible(true);
        const hideT = setTimeout(() => setIsVisible(false), 5000);
        timeoutsRef.current.push(hideT);
      }, cumulativeDelay);
      timeoutsRef.current.push(showT);
      cumulativeDelay += intervals[i % intervals.length];
    });
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, [language]);
  return <AnimatePresence data-id="element-94">
      {isVisible && <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="fixed bottom-6 left-0 right-0 z-50 flex justify-center px-4" data-id="element-95">
          <div className="bg-gradient-to-br from-[#2C4A3E] to-[#1F3329] rounded-xl shadow-2xl border border-[#D4A574]/30 overflow-hidden max-w-md w-full" data-id="element-96">
            <div className="flex items-center gap-4 p-4" data-id="element-97">
              {/* Book Cover Image */}
              <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden shadow-lg" data-id="element-98">
                <img src="https://cdn.magicpatterns.com/uploads/nBxrSidjbsDSAggLesjYdU/ChatGPT_Image_23_nov_2025,_20_10_54.webp" alt="Book cover" className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-99" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-100">
                <div className="flex items-center gap-2 mb-1" data-id="element-101">
                  <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]" data-id="element-102" />
                  <p className="text-[#D4A574] text-sm font-medium" data-id="element-103">
                    {t.notification.recentPurchase}
                  </p>
                </div>
                <p className="text-white font-semibold text-base mb-1" data-id="element-104">
                  {current.name}{' '}
                  <span className="text-white/70 font-normal" data-id="element-105">
                    {t.notification.from} {current.location}
                  </span>
                </p>
                {current.type === 'ebook' ? <p className="text-[#D4A574] text-sm" data-id="element-106">
                    {t.notification.ebook} •{' '}
                    <span className="font-semibold" data-id="element-107">$16.97</span>
                  </p> : <div className="space-y-0.5" data-id="element-108">
                    <p className="text-[#D4A574] text-sm" data-id="element-109">
                      {t.notification.ebook} •{' '}
                      <span className="font-semibold" data-id="element-110">$16.97</span>
                    </p>
                    <div className="flex items-center gap-1.5" data-id="element-111">
                      <PlusIcon className="w-3 h-3 text-[#E8B86D]" data-id="element-112" />
                      <p className="text-[#E8B86D] text-xs flex items-center gap-1" data-id="element-113">
                        <HeadphonesIcon className="w-3 h-3" data-id="element-114" />
                        {isEs ? 'Audio Guiado' : 'Guided Audio'} •{' '}
                        <span className="font-semibold" data-id="element-115">$9.97</span>
                      </p>
                    </div>
                  </div>}
              </div>

              {/* Close Button */}
              <button onClick={() => setIsVisible(false)} className="flex-shrink-0 text-white/50 hover:text-white transition-colors p-1" aria-label="Close notification" data-id="element-116">
                <X className="w-5 h-5" data-id="element-117" />
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}